import { useEffect, useRef } from 'react';
import { ExternalLink, Loader2, FileX2 } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetBody,
  SheetFooter,
  SheetClose,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Empty } from '@/components/ui/empty';
import { usePreviewItems } from '@/lib/hooks/prestacoes';
import { fmtBRL, fmtDate, fmtCpfCnpj } from '@/lib/utils';

const KIND_LABEL: Record<string, string> = {
  pagamentos: 'Pagamentos',
  receitas: 'Receitas',
  empregados: 'Empregados',
  bens: 'Bens',
  contratos: 'Contratos',
  'documentos-fiscais': 'Documentos Fiscais',
};

const KIND_ROUTE: Record<string, string> = {
  pagamentos: '/lancamentos/pagamentos',
  receitas: '/lancamentos/receitas',
  empregados: '/lancamentos/empregados',
  bens: '/lancamentos/bens',
  contratos: '/lancamentos/contratos',
  'documentos-fiscais': '/lancamentos/documentos-fiscais',
};

/** Formata um campo de item de forma genérica conforme chave */
function renderField(key: string, value: unknown): string {
  if (value == null) return '—';
  if (key === 'valor' || key === 'valorTotal' || key === 'valorRepasse') {
    return fmtBRL(value as string | number);
  }
  if (key === 'cpf' || key === 'cnpj' || key === 'cpfCnpj') {
    return fmtCpfCnpj(String(value));
  }
  if (key === 'data' || key === 'dataEmissao' || key === 'dataAdmissao') {
    return fmtDate(String(value));
  }
  return String(value);
}

/** Campos prioritários por kind para exibir em destaque */
const KIND_FIELDS: Record<string, string[]> = {
  pagamentos: ['beneficiario', 'cpfCnpj', 'valor', 'data', 'descricao'],
  receitas: ['descricao', 'valor', 'data'],
  empregados: ['nome', 'cpf', 'cargo', 'dataAdmissao'],
  bens: ['descricao', 'valorTotal', 'numero'],
  contratos: ['fornecedor', 'cnpj', 'objeto', 'valorTotal'],
  'documentos-fiscais': ['fornecedor', 'cnpj', 'valor', 'dataEmissao', 'numero'],
};

function ItemRow({ item, kind }: { item: Record<string, unknown>; kind: string }) {
  const fields = KIND_FIELDS[kind] ?? Object.keys(item).filter((k) => k !== 'id' && k !== 'kind');

  return (
    <div className="rounded-2 border border-card-stroke bg-card-soft p-3">
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5">
        {fields.map((key) => {
          const raw = item[key];
          if (raw == null) return null;
          return (
            <div key={key} className="col-span-1">
              <dt className="text-[10px] font-mono uppercase tracking-wider text-fg-dim">
                {key}
              </dt>
              <dd className="text-[13px] font-medium text-fg leading-tight mt-0.5 truncate">
                {renderField(key, raw)}
              </dd>
            </div>
          );
        })}
      </dl>
    </div>
  );
}

function SkeletonItemRow() {
  return (
    <div className="rounded-2 border border-card-stroke bg-card-soft p-3 space-y-2" aria-hidden>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="space-y-1">
            <div className="skel h-2 w-16 rounded-1" />
            <div className="skel h-3.5 w-28 rounded-1" />
          </div>
        ))}
      </div>
    </div>
  );
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  kind: string;
  acordoId: string;
  competencia: string;
  title: string;
}

export function DrillDownDrawer({
  open,
  onOpenChange,
  workspaceId,
  kind,
  acordoId,
  competencia,
  title,
}: Props) {
  const sentinelRef = useRef<HTMLDivElement>(null);

  const q = usePreviewItems(workspaceId, { kind, acordoId, competencia, limit: 50 });

  const allItems = q.data?.pages.flatMap((p) => p.items) ?? [];
  const total = q.data?.pages[0]?.total ?? 0;

  // Infinite scroll via IntersectionObserver no sentinel
  useEffect(() => {
    if (!sentinelRef.current) return;
    const el = sentinelRef.current;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && q.hasNextPage && !q.isFetchingNextPage) {
          q.fetchNextPage();
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [q]);

  const kindLabel = KIND_LABEL[kind] ?? kind;
  const editRoute = KIND_ROUTE[kind];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent width="w-[min(92vw,640px)]">
        <SheetHeader
          eyebrow={kindLabel}
          title={title}
          description={total > 0 ? `${total} registro${total === 1 ? '' : 's'} encontrado${total === 1 ? '' : 's'}` : undefined}
        />

        <SheetBody className="space-y-2.5">
          {/* Loading inicial */}
          {q.isLoading && (
            <>
              <SkeletonItemRow />
              <SkeletonItemRow />
              <SkeletonItemRow />
              <SkeletonItemRow />
            </>
          )}

          {/* Empty state */}
          {!q.isLoading && allItems.length === 0 && (
            <Empty
              icon={FileX2}
              title={`Nenhum registro em ${kindLabel.toLowerCase()}`}
              description="Nada encontrado para este convênio e competência."
              variant="soft"
            />
          )}

          {/* Lista */}
          {allItems.map((item) => (
            <ItemRow key={item.id} item={item as Record<string, unknown>} kind={kind} />
          ))}

          {/* Sentinel de infinite scroll */}
          <div ref={sentinelRef} className="h-4" aria-hidden />

          {/* Loading paginação */}
          {q.isFetchingNextPage && (
            <div className="flex items-center justify-center gap-2 py-3 text-sm text-fg-muted">
              <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.8} />
              Carregando mais…
            </div>
          )}

          {/* Fim da lista */}
          {!q.hasNextPage && allItems.length > 0 && (
            <p className="text-center text-[11px] font-mono text-fg-dim py-2">
              — fim da lista —
            </p>
          )}
        </SheetBody>

        <SheetFooter>
          <SheetClose asChild>
            <Button variant="ghost" type="button">Fechar</Button>
          </SheetClose>
          {editRoute && (
            <Button
              variant="default"
              type="button"
              onClick={() => window.open(editRoute, '_blank', 'noopener')}
            >
              <ExternalLink className="h-4 w-4" strokeWidth={1.8} />
              Editar em {kindLabel}
            </Button>
          )}
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
